import { Card, Colors, Heading, Icon, Pills, Text } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';

type MeetingPlanTemplateCardProps = {
    title: string;
    objective: string;
    onClick: () => void;
    /** A draft is kept for its editors and never offered to managers; the tile says so. */
    isDraft?: boolean;
};

/**
 * One meeting plan template, as the Templates page shows it: what the conversation is called and
 * what it is for. The whole tile is the button that opens the plan's editor.
 */
export const MeetingPlanTemplateCard = ({
    title,
    objective,
    onClick,
    isDraft = false,
}: MeetingPlanTemplateCardProps) => {
    const { t } = useAmelioTranslations();

    return (
        // The card the section sits in folds it on any click it receives, so the tile keeps its own:
        // opening a plan must never also close the list it was opened from. The library's Card takes a
        // handler with no event, hence the wrapper rather than a stopPropagation inside onClick.
        <div
            className={'h-full'}
            onClick={(event) => event.stopPropagation()}
        >
            <Card
                interactive
                onClick={onClick}
                bgColor={'bg-white/70'}
                borderRadius={'2xl'}
                borderColor={Colors.WHITE}
                className={'h-full w-full border-2 text-left'}
            >
                <div className={'flex items-start justify-between gap-2'}>
                    <div className={'bg-secondary-50 w-fit rounded-md p-3'}>
                        <Icon
                            name={'messages-message-circle'}
                            size={32}
                            color={Colors.SECONDARY}
                            ariaHidden
                        />
                    </div>

                    {isDraft && (
                        <Pills
                            variant={'draftLight'}
                            size={'tinted'}
                        >
                            {t('common.draft')}
                        </Pills>
                    )}
                </div>

                <Heading
                    level={6}
                    className={'mt-4'}
                >
                    {title}
                </Heading>

                <Text
                    className={'mt-2'}
                    size={'small'}
                    color={Colors.BASE_500}
                >
                    {objective}
                </Text>
            </Card>
        </div>
    );
};
